/**
 * FieldAlertBar — Notification bell for field alerts (SOS, Flag, More Time)
 *
 * Sits in the office nav icon row alongside Quiz Bell, Voting, and Mentions.
 * Shows a radio tower icon with badge count of active alerts.
 * Click opens a dropdown panel with alert details + action buttons.
 *
 * - SOS: Red severity — most prominent
 * - Flag: Amber severity
 * - More Time: Orange severity
 *
 * Auto-hides when no active alerts (returns null).
 */

import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Radio,
  AlertTriangle,
  Flag,
  Clock,
  Check,
  X,
  MapPin,
} from 'lucide-react';
import { useSOSAlerts } from '@/hooks/useSOSAlerts';
import { useFlagAlerts } from '@/hooks/useFlagAlerts';
import { useTimeRequestAlerts } from '@/hooks/useTimeRequestAlerts';
import {
  acknowledgeSOS,
  resolveSOS,
  acknowledgeFlag,
  dismissFlag,
  acknowledgeRequestMoreTime,
  clearRequestMoreTime,
} from '@/services/sos.service';

interface FieldAlertBarProps {
  tenantId: string | null;
}

export function FieldAlertBar({ tenantId }: FieldAlertBarProps) {
  const navigate = useNavigate();
  const { alerts: sosAlerts } = useSOSAlerts(tenantId);
  const { alerts: flagAlerts } = useFlagAlerts(tenantId);
  const { alerts: timeAlerts } = useTimeRequestAlerts(tenantId);
  const [showPanel, setShowPanel] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  const userId = localStorage.getItem('user_id') || localStorage.getItem('logged_in_user') || 'office';

  const totalAlerts = sosAlerts.length + flagAlerts.length + timeAlerts.length;
  const hasSOS = sosAlerts.length > 0;

  // Close panel on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        panelRef.current &&
        buttonRef.current &&
        !panelRef.current.contains(event.target as Node) &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setShowPanel(false);
      }
    };

    if (showPanel) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showPanel]);

  // Nothing to show — hide completely
  if (totalAlerts === 0) return null;

  return (
    <div className="relative">
      {/* Bell button */}
      <button
        ref={buttonRef}
        onClick={() => setShowPanel(!showPanel)}
        className={`
          ${hasSOS ? 'bg-red-500/20 ring-2 ring-red-500/40 shadow-md shadow-red-500/10' : 'bg-white/[0.06] hover:bg-white/15'}
          p-2 rounded-lg transition-all active:scale-95 relative
        `}
        title={`${totalAlerts} field alert${totalAlerts !== 1 ? 's' : ''}`}
      >
        <Radio
          className={`w-4 h-4 transition-colors ${
            hasSOS ? 'text-red-400 animate-pulse' : 'text-amber-400'
          }`}
        />
        <span className={`absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full border-2 border-brand-dark shadow-sm flex items-center justify-center ${
          hasSOS ? 'bg-red-500' : 'bg-amber-500'
        }`}>
          <span className="text-[9px] font-bold text-white">
            {totalAlerts > 99 ? '99+' : totalAlerts}
          </span>
        </span>
      </button>

      {/* Dropdown panel */}
      {showPanel && (
        <div
          ref={panelRef}
          className="absolute right-0 top-full mt-2 w-[360px] bg-surface-card border border-theme rounded-2xl shadow-2xl overflow-hidden z-50 backdrop-blur-xl"
        >
          {/* Header */}
          <div className="px-4 py-3 border-b border-theme flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Radio className="w-4 h-4 text-theme-secondary" />
              <span className="text-sm font-bold text-theme-primary">Field Alerts</span>
            </div>
            <div className="flex items-center gap-2">
              {sosAlerts.length > 0 && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-red-500/15 text-red-500 text-[10px] font-bold">
                  <AlertTriangle className="w-3 h-3" /> {sosAlerts.length}
                </span>
              )}
              {flagAlerts.length > 0 && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-500 text-[10px] font-bold">
                  <Flag className="w-3 h-3" /> {flagAlerts.length}
                </span>
              )}
              {timeAlerts.length > 0 && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-orange-500/15 text-orange-500 text-[10px] font-bold">
                  <Clock className="w-3 h-3" /> {timeAlerts.length}
                </span>
              )}
            </div>
          </div>

          {/* Alert list */}
          <div className="max-h-[400px] overflow-y-auto divide-y divide-theme-soft">
            {sosAlerts.map((alert) => (
              <AlertRow
                key={alert.id}
                type="sos"
                techName={alert.techName}
                reason={alert.reason}
                customerName={alert.customerName}
                address={alert.address}
                time={alert.triggeredAt}
                acknowledged={alert.acknowledged}
                onAcknowledge={async () => {
                  if (alert.id) await acknowledgeSOS(alert.id, userId, alert.jobId);
                }}
                onResolve={async () => {
                  if (alert.id) await resolveSOS(alert.id, alert.jobId);
                }}
                onNavigate={() => {
                  setShowPanel(false);
                  navigate('/home/schedule');
                }}
              />
            ))}

            {flagAlerts.map((alert) => (
              <AlertRow
                key={alert.id}
                type="flag"
                techName={alert.techName}
                reason={alert.reason}
                customerName={alert.customerName}
                address={alert.address}
                time={alert.flaggedAt}
                acknowledged={alert.acknowledged}
                onAcknowledge={async () => {
                  if (alert.id) await acknowledgeFlag(alert.id, userId, alert.jobId);
                }}
                onResolve={async () => {
                  await dismissFlag(alert.jobId, alert.id);
                }}
                onNavigate={() => {
                  setShowPanel(false);
                  navigate('/home/schedule');
                }}
              />
            ))}

            {timeAlerts.map((alert) => (
              <AlertRow
                key={alert.id}
                type="time"
                techName={alert.techName}
                reason={alert.reason}
                customerName={alert.customerName}
                address={alert.address}
                time={alert.requestedAt}
                acknowledged={alert.acknowledged}
                extraMinutes={alert.extraMinutes}
                onAcknowledge={async () => {
                  await acknowledgeRequestMoreTime(alert.jobId, userId, alert.id);
                }}
                onResolve={async () => {
                  await clearRequestMoreTime(alert.jobId, alert.id);
                }}
                onNavigate={() => {
                  setShowPanel(false);
                  navigate('/home/schedule');
                }}
              />
            ))}
          </div>

          {/* Triangle pointer */}
          <div className="absolute -top-2 right-2 w-4 h-4 bg-surface-card border-t border-l border-theme transform rotate-45" />
        </div>
      )}
    </div>
  );
}

// ─── Alert Row ───────────────────────────────────────────────────────────────

interface AlertRowProps {
  type: 'sos' | 'flag' | 'time';
  techName: string;
  reason: string;
  customerName: string;
  address: string;
  time: string;
  acknowledged: boolean;
  extraMinutes?: number;
  onAcknowledge: () => Promise<void>;
  onResolve: () => Promise<void>;
  onNavigate: () => void;
}

const TYPE_CONFIG = {
  sos: { icon: AlertTriangle, color: 'text-red-500', bg: 'bg-red-500/10', label: 'SOS' },
  flag: { icon: Flag, color: 'text-amber-500', bg: 'bg-amber-500/10', label: 'FLAG' },
  time: { icon: Clock, color: 'text-orange-500', bg: 'bg-orange-500/10', label: 'TIME' },
};

function AlertRow({
  type,
  techName,
  reason,
  customerName,
  address,
  time,
  acknowledged,
  extraMinutes,
  onAcknowledge,
  onResolve,
  onNavigate,
}: AlertRowProps) {
  const [acting, setActing] = useState(false);
  const config = TYPE_CONFIG[type];
  const Icon = config.icon;
  const timeAgo = getTimeAgo(time);

  const handleAction = async (action: () => Promise<void>) => {
    setActing(true);
    try {
      await action();
    } catch (err) {
      console.error(`Alert action failed:`, err);
    } finally {
      setActing(false);
    }
  };

  return (
    <div className={`px-4 py-3 ${config.bg} flex items-start gap-3`}>
      <div className={`mt-0.5 shrink-0 ${config.color}`}>
        <Icon className="w-4 h-4" />
      </div>

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-0.5">
          <span className={`text-[10px] font-bold uppercase tracking-wider ${config.color}`}>{config.label}</span>
          <span className="text-xs font-bold text-theme-primary">{techName}</span>
          <span className="text-[10px] text-theme-muted">{timeAgo}</span>
        </div>
        <p className="text-xs text-theme-secondary truncate">
          {reason}
          {type === 'time' && extraMinutes && <span className="text-orange-500 font-semibold"> +{extraMinutes}m</span>}
        </p>
        <button
          onClick={onNavigate}
          className="text-[11px] text-theme-muted hover:text-blue-500 transition-colors flex items-center gap-1 mt-0.5"
        >
          <MapPin className="w-3 h-3" />
          {customerName} — {address}
        </button>
      </div>

      <div className="flex items-center gap-1.5 shrink-0">
        {!acknowledged ? (
          <button
            onClick={() => handleAction(onAcknowledge)}
            disabled={acting}
            className="px-2 py-1 rounded-md bg-emerald-500/15 text-emerald-500 text-[10px] font-bold hover:bg-emerald-500/25 transition-colors disabled:opacity-40"
            title="Acknowledge"
          >
            <Check className="w-3 h-3" />
          </button>
        ) : (
          <span className="text-[10px] text-emerald-500 font-medium px-1">ACK</span>
        )}
        <button
          onClick={() => handleAction(onResolve)}
          disabled={acting}
          className="px-2 py-1 rounded-md bg-gray-500/15 text-theme-muted text-[10px] font-bold hover:bg-gray-500/25 transition-colors disabled:opacity-40"
          title="Resolve"
        >
          <X className="w-3 h-3" />
        </button>
      </div>
    </div>
  );
}

function getTimeAgo(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  return `${hrs}h ago`;
}
